// --- Chain name type ---

export const ALL_CHAIN_NAMES = [
  "Ethereum",
  "Solana",
  "Aptos",
  "Sui",
  "Base",
  "Arbitrum",
  "Sepolia",
  "ArbitrumSepolia",
  "BaseSepolia",
] as const;

export type ChainName = (typeof ALL_CHAIN_NAMES)[number];

// --- EVM chain names (pure data, no UI dependencies) ---

/** All known EVM chain display names */
export const EVM_CHAIN_NAMES = ["Ethereum", "Arbitrum", "Base"] as const;

export type EvmChainName = (typeof EVM_CHAIN_NAMES)[number];

/** Set for fast lookup */
const EVM_CHAIN_NAME_SET: ReadonlySet<string> = new Set(EVM_CHAIN_NAMES);

/** Check if a chain name refers to a supported EVM chain */
export function isEvmChain(chainName: string): boolean {
  return EVM_CHAIN_NAME_SET.has(chainName);
}

/**
 * Whether the connected wallet is an Aptos wallet the user can open, as opposed to the derived
 * Aptos account a Solana/EVM wallet signs through. An EVM wallet reports `chainName` "Aptos" while
 * its chain id loads, so `isEVM` has to veto it.
 */
export function isNativeAptosWallet(wallet: {
  chainName: string;
  isEVM: boolean;
  isOverriddenAptosWallet: boolean;
}): boolean {
  return wallet.isOverriddenAptosWallet || (!wallet.isEVM && wallet.chainName === "Aptos");
}

// --- Server-side claim chains ---

/**
 * Chains where the server handles the destination claim transaction
 * (users don't need to sign or pay gas on the destination chain).
 * Add new chains here when server-side fee payer support is enabled.
 */
const SERVER_CLAIM_CHAINS: ReadonlySet<string> = new Set(["Solana"]);

/** Check if a chain supports server-side claiming (no manual user claim needed) */
export function hasServerClaim(chainName: string): boolean {
  return SERVER_CLAIM_CHAINS.has(chainName);
}

const MAINNET_EVM_CHAIN_IDS: Record<number, EvmChainName> = {
  1: "Ethereum",
  42161: "Arbitrum",
  8453: "Base",
};

// Circle's testnet CCTP v2 contracts live here, so testnet v2 burns and claims switch to them.
const SEPOLIA_EVM_CHAIN_IDS: Record<number, EvmChainName> = {
  11155111: "Ethereum",
  421614: "Arbitrum",
  84532: "Base",
};

function evmChainNameForId(
  chainId: number,
  network: "mainnet" | "testnet",
): EvmChainName | undefined {
  return (
    MAINNET_EVM_CHAIN_IDS[chainId] ??
    (network === "testnet" ? SEPOLIA_EVM_CHAIN_IDS[chainId] : undefined)
  );
}

/** Whether the wallet may stay on `chainId`; anything else is switched back to a supported chain. */
export function isSupportedEvmChainId(chainId: number, network: "mainnet" | "testnet"): boolean {
  return evmChainNameForId(chainId, network) !== undefined;
}

/** Map EVM chainId to ChainName, a Sepolia chain by its mainnet chain. Unsupported chains default to Ethereum. */
export function evmChainIdToName(chainId: number, network: "mainnet" | "testnet"): ChainName {
  return evmChainNameForId(chainId, network) ?? "Ethereum";
}

export const validateChainName = (chainName: string | null | undefined): ChainName | undefined => {
  if (!chainName) return undefined;
  return ALL_CHAIN_NAMES.find((name) => name.toLowerCase() === chainName.toLowerCase());
};
